import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useGetPageTextContent } from "~/api/documents/documents";
import { useGetKnowledgebasePageContent } from "~/api/knowledge/knowledge";
import type { Language, ReportSectionMeta } from "~/api/model";
import { HighlightedMarkdown } from "~/components/HighlightedMarkdown";
import { MarkdownContentModal } from "~/components/MarkdownContentModal";
import {
  buildTermMap,
  TermDefinitionDialog,
} from "~/components/TermDefinitionDialog";
import { Accordion } from "~/components/ui/accordion";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import { useDocList } from "~/features/chat/hooks/useDocList";
import { useMedicationsStore } from "~/features/chat/stores/medicationsStore";
import { useChatDocumentParams } from "~/helpers";
import { trackEvent } from "~/utils/analytics";
import {
  BatesLink,
  type SectionClickHandler,
  SectionLink,
  useSourceMapDelegation,
} from "~/utils/markdownTerms";
import { getMedicationMatcher } from "~/utils/termMatcher";
import { Hint } from "../../types";
import {
  getInitialExpandedSections,
  INITIAL_VISIBLE_SECTION_COUNT,
} from "./getInitialExpandedSections";
import { ReportContainerContext } from "./reportContainerContext";
import { ReportGenerationProgress } from "./ReportGenerationProgress";
import { buildReportReferenceDocuments } from "./reportReferenceSlashCommand";
import { ReportSectionMdastView } from "./ReportSectionMdastView";
import { SourceReference } from "./ReportSectionSources";
import { OutdatedReason } from "./TemplateReportSelector";

export type ReportViewStatus =
  | { type: "loading" }
  | { type: "processing" }
  | { type: "pending" }
  | { type: "noReportSelected" }
  | { type: "ready" }
  | { type: "outdated"; reason?: OutdatedReason }
  | { type: "failed" }
  | { type: "contentError" }
  | { type: "empty" };

type ReportStatusAlertProps = {
  titleId: string;
  descriptionId: string;
  variant: "default" | "destructive";
  action?: {
    labelId: string;
    onClick: () => void;
  };
};

const ReportStatusAlert = ({
  titleId,
  descriptionId,
  variant,
  action,
}: ReportStatusAlertProps) => (
  <Alert variant={variant}>
    <AlertTitle>
      <FormattedMessage id={titleId} />
    </AlertTitle>
    <AlertDescription
      className={action ? "flex flex-col items-start gap-3" : undefined}
    >
      <span>
        <FormattedMessage id={descriptionId} />
      </span>
      {action && (
        <Button variant="outline" size="sm" onClick={action.onClick}>
          <FormattedMessage id={action.labelId} />
        </Button>
      )}
    </AlertDescription>
  </Alert>
);

type ReportViewProps = {
  reportSections: ReportSectionMeta[];
  status: ReportViewStatus;
  terms: Hint[];
  sectionLinks?: SectionLink[];
  content: string;
  reportId: string;
  templateName?: string | null;
  outputLanguage?: Language | null;
  isActionsAvailable?: boolean;
  onSectionClick?: SectionClickHandler;
  batesLinks?: BatesLink[];
  onBatesClick?: (pageId: string) => void;
  onRetryGeneration?: () => void;
};

export const ReportView = ({
  reportSections,
  status,
  terms,
  sectionLinks = [],
  content,
  reportId,
  templateName,
  outputLanguage,
  isActionsAvailable = true,
  onSectionClick,
  batesLinks,
  onBatesClick,
  onRetryGeneration,
}: ReportViewProps) => {
  const { formatMessage } = useIntl();
  const isReportOutdated = status.type === "outdated";
  const failedReportAlert = (
    <ReportStatusAlert
      titleId="chat.report.generationFailed"
      descriptionId="chat.report.generationFailedDescription"
      variant="destructive"
      action={
        onRetryGeneration
          ? { labelId: "chat.report.tryAgain", onClick: onRetryGeneration }
          : undefined
      }
    />
  );
  const { organizationId, caseId } = useChatDocumentParams();
  const { docList, pageItems, absolutePageNumbers } = useDocList();
  const referenceDocuments = useMemo(
    () =>
      buildReportReferenceDocuments(docList, {
        formatPageLabel: (pageNumber) =>
          formatMessage(
            {
              id: "report.edit.reference.pageLabel",
              defaultMessage: "Page {pageNumber}",
            },
            { pageNumber }
          ),
      }),
    [docList, formatMessage]
  );

  const [reportContainerEl, setReportContainerEl] =
    useState<HTMLDivElement | null>(null);
  useSourceMapDelegation(reportContainerEl, onSectionClick, onBatesClick);

  const medications = useMedicationsStore((s) => s.medications);
  const medicationMatcher = useMemo(
    () => getMedicationMatcher(medications),
    [medications]
  );
  const termMap = useMemo(
    () => buildTermMap(terms, medications),
    [terms, medications]
  );

  // Source modal state
  const [pageTextModalOpen, setPageTextModalOpen] = useState(false);
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
  const [isContentLoading, setIsContentLoading] = useState(false);
  const [pageImageUrl, setPageImageUrl] = useState<string | undefined>(
    undefined
  );
  const [pageRotation, setPageRotation] = useState(0);

  // Knowledge base source state
  const [selectedKbDocumentId, setSelectedKbDocumentId] = useState<
    string | null
  >(null);
  const [selectedKbPageNumber, setSelectedKbPageNumber] = useState<
    string | null
  >(null);
  const [isKnowledgeBaseSource, setIsKnowledgeBaseSource] = useState(false);

  const getSectionValue = useCallback(
    (section: ReportSectionMeta) => section.id,
    []
  );

  const sortedSections = useMemo(
    () => [...reportSections].sort((a, b) => a.order - b.order),
    [reportSections]
  );

  const completedSectionIds = useCallback(
    () =>
      sortedSections
        .filter((s) => s.status === "completed")
        .map((s) => getSectionValue(s)),
    [sortedSections, getSectionValue]
  );

  const [expandedSections, setExpandedSections] = useState<string[]>(() =>
    getInitialExpandedSections(completedSectionIds())
  );

  const prevSectionsRef = useRef<{
    ids: Set<string>;
    statuses: Record<string, string>;
  }>({
    ids: new Set(sortedSections.map((s) => getSectionValue(s))),
    statuses: Object.fromEntries(
      sortedSections.map((s) => [getSectionValue(s), s.status])
    ),
  });

  useEffect(() => {
    const currentIds = sortedSections.map((s) => getSectionValue(s));
    const currentSet = new Set(currentIds);
    const prev = prevSectionsRef.current;

    const hasRemovedIds = [...prev.ids].some((id) => !currentSet.has(id));

    if (hasRemovedIds) {
      setExpandedSections(getInitialExpandedSections(completedSectionIds()));
    } else {
      const toExpand: string[] = [];
      const toCollapse: string[] = [];

      for (const section of sortedSections) {
        const id = getSectionValue(section);
        const prevStatus = prev.statuses[id];
        const isNew = !prev.ids.has(id);

        if (isNew && section.status === "completed") {
          toExpand.push(id);
        } else if (
          !isNew &&
          section.status === "completed" &&
          prevStatus !== "completed"
        ) {
          toExpand.push(id);
        } else if (
          !isNew &&
          (section.status === "processing" || section.status === "pending") &&
          (prevStatus === "completed" || prevStatus === "failed")
        ) {
          toCollapse.push(id);
        }
      }

      if (toExpand.length > 0 || toCollapse.length > 0) {
        setExpandedSections((prev) => {
          const collapseSet = new Set(toCollapse);
          return [...prev.filter((id) => !collapseSet.has(id)), ...toExpand];
        });
      }
    }

    prevSectionsRef.current = {
      ids: currentSet,
      statuses: Object.fromEntries(
        sortedSections.map((s) => [getSectionValue(s), s.status])
      ),
    };
  }, [sortedSections, getSectionValue, completedSectionIds]);

  // Fetch case document page content
  const {
    data: pageContentData,
    isLoading: isPageContentLoading,
    error: pageContentError,
  } = useGetPageTextContent(organizationId, caseId, selectedPageId ?? "", {
    query: {
      enabled:
        !isKnowledgeBaseSource &&
        !!selectedPageId &&
        !!caseId &&
        selectedPageId !== "unknown" &&
        organizationId !== "unknown",
    },
  });

  // Fetch knowledge base document page content
  const {
    data: kbPageContentData,
    isLoading: isKbPageContentLoading,
    error: kbPageContentError,
  } = useGetKnowledgebasePageContent(
    organizationId,
    selectedKbDocumentId ?? "",
    selectedKbPageNumber ?? "",
    {
      query: {
        enabled:
          isKnowledgeBaseSource &&
          !!selectedKbDocumentId &&
          !!selectedKbPageNumber &&
          !!selectedKbPageNumber &&
          organizationId !== "unknown",
      },
    }
  );

  const handleSourceClick = useCallback(
    async (source: SourceReference) => {
      trackEvent({
        eventName: "app/report/section_source.badge/click",
        properties: {
          isKnowledgeBase: source.isKnowledgeBase === true,
          documentId: source.documentId,
        },
      });
      const isKbSource = source.isKnowledgeBase === true;

      if (isKbSource) {
        // Knowledge base source - pageId is the page number, documentId is the knowledgeDocumentId
        if (source.pageId && source.documentId) {
          setIsKnowledgeBaseSource(true);
          setSelectedKbDocumentId(source.documentId);
          setSelectedKbPageNumber(source.pageId);
          setSelectedPageId(null);
          setPageImageUrl(undefined);
          setPageRotation(0);
          setIsContentLoading(false);
          setPageTextModalOpen(true);
        }
      } else {
        // Case document source - pageId is the page UUID
        setIsKnowledgeBaseSource(false);
        setSelectedKbDocumentId(null);
        setSelectedKbPageNumber(null);
        setSelectedPageId(source.pageId);
        setIsContentLoading(true);
        setPageTextModalOpen(true);

        const pageItem = pageItems.find((p) => p.pageId === source.pageId);
        if (pageItem) {
          setPageRotation(pageItem.rotate);

          try {
            const urls = await pageItem.getUrls();
            setPageImageUrl(urls.retinaUrl || urls.highResUrl || undefined);
          } catch {
            setPageImageUrl(undefined);
          } finally {
            setIsContentLoading(false);
          }
        } else {
          setIsContentLoading(false);
        }
      }
    },
    [pageItems]
  );

  const modalTitle = useMemo(() => {
    if (isKnowledgeBaseSource) {
      return `${kbPageContentData?.filename ?? "Knowledge Base Document"} - Page ${(kbPageContentData?.pageNumber ?? 0) + 1}`;
    }

    if (!selectedPageId) return "Page Content Recognition";

    const pageItem = pageItems.find((p) => p.pageId === selectedPageId);
    if (!pageItem) return "Page Content Recognition";

    const section = docList?.tree
      .flatMap((doc) => doc.sections)
      .find((sec) => sec.id === pageItem.sectionId);

    const sectionName = section?.name || "Unknown Section";
    const pageNumber = absolutePageNumbers[selectedPageId] || 0;

    return `${sectionName} – Page ${pageNumber}`;
  }, [
    isKnowledgeBaseSource,
    kbPageContentData?.filename,
    kbPageContentData?.pageNumber,
    selectedPageId,
    pageItems,
    docList,
    absolutePageNumbers,
  ]);

  if (
    (status.type === "loading" || status.type === "processing") &&
    reportSections.length === 0
  ) {
    return (
      <div>
        <ReportGenerationProgress
          reportId={reportId}
          sections={reportSections}
          isGenerating={status.type === "processing"}
        />
        <div className="max-w-sm animate-pulse">
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index}>
              {index % 2 === 0 ? (
                <Skeleton className="h-4 bg-muted rounded-full w-48 mb-4" />
              ) : (
                <Skeleton className="h-4 bg-muted rounded-full w-32 mb-4" />
              )}
              <Skeleton className="h-3 bg-muted rounded-full max-w-[360px] mb-2.5" />
              <Skeleton className="h-3 bg-muted rounded-full mb-2.5" />
              <Skeleton className="h-3 bg-muted rounded-full max-w-[330px] mb-2.5" />
              <Skeleton className="h-3 bg-muted rounded-full max-w-[300px] mb-2.5" />
              <Skeleton className="h-3 bg-muted rounded-full max-w-[360px] mb-6" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (reportSections.length > 0) {
    return (
      <ReportContainerContext.Provider value={reportContainerEl}>
        <div ref={setReportContainerEl} className="flex flex-col gap-4">
          <TermDefinitionDialog
            containerRef={reportContainerEl}
            termMap={termMap}
          />
          {status.type === "failed" && failedReportAlert}
          <ReportGenerationProgress
            reportId={reportId}
            sections={reportSections}
            isGenerating={status.type === "processing"}
          />
          <Accordion
            type="multiple"
            value={expandedSections}
            onValueChange={(newValue: string[]) => {
              const expanded = newValue.filter(
                (v) => !expandedSections.includes(v)
              );
              const collapsed = expandedSections.filter(
                (v) => !newValue.includes(v)
              );
              if (expanded.length > 0 || collapsed.length > 0) {
                trackEvent({
                  eventName: "app/report/section_accordion.trigger/click",
                  properties: {
                    expanded: expanded.length > 0 ? expanded : undefined,
                    collapsed: collapsed.length > 0 ? collapsed : undefined,
                  },
                });
              }
              setExpandedSections(newValue);
            }}
          >
            {sortedSections.map((section, index) => {
              const sectionValue = getSectionValue(section);
              return (
                <ReportSectionMdastView
                  key={sectionValue}
                  reportSection={section}
                  accordionValue={sectionValue}
                  terms={terms}
                  medicationMatcher={medicationMatcher}
                  sectionLinks={sectionLinks}
                  reportId={reportId}
                  isExpanded={expandedSections.includes(sectionValue)}
                  templateName={templateName}
                  outputLanguage={outputLanguage}
                  isActionAvailable={isActionsAvailable}
                  isReportOutdated={isReportOutdated}
                  initiallyVisible={index < INITIAL_VISIBLE_SECTION_COUNT}
                  onSourceClick={handleSourceClick}
                  onSectionClick={onSectionClick}
                  batesLinks={batesLinks}
                  onBatesClick={onBatesClick}
                  referenceDocuments={referenceDocuments}
                />
              );
            })}
          </Accordion>

          {pageTextModalOpen && (
            <MarkdownContentModal
              title={modalTitle}
              content={
                isKnowledgeBaseSource
                  ? isKbPageContentLoading
                    ? "Loading content..."
                    : kbPageContentError
                      ? "Error loading content. This page may not have processed content available yet."
                      : kbPageContentData?.content ||
                        "No content available for this page."
                  : isPageContentLoading || isContentLoading
                    ? "Loading content..."
                    : pageContentError
                      ? "Error loading content. This page may not have processed content available yet."
                      : pageContentData?.content ||
                        "No content available for this page."
              }
              filename={
                isKnowledgeBaseSource
                  ? kbPageContentData?.filename
                  : pageContentData?.filename
              }
              authorName={
                isKnowledgeBaseSource
                  ? (kbPageContentData?.authorName ?? undefined)
                  : pageContentData?.authorName
              }
              uploadDate={
                isKnowledgeBaseSource
                  ? kbPageContentData?.uploadDate
                  : pageContentData?.uploadDate
              }
              open={pageTextModalOpen}
              onOpenChange={(open) => {
                setPageTextModalOpen(open);
                if (!open) {
                  // Reset state when modal closes
                  setIsKnowledgeBaseSource(false);
                  setSelectedKbDocumentId(null);
                  setSelectedKbPageNumber(null);
                  setSelectedPageId(null);
                  setPageImageUrl(undefined);
                  setPageRotation(0);
                }
              }}
              copyEventName="app/report/page_text_modal/copy_all.button/click"
              exportOpenEventName="app/report/page_text_modal/export.button/click"
              exportEventName="app/report/page_text_modal/export_menu_open/export_action.link/click"
              terms={terms}
              pageImageUrl={
                isKnowledgeBaseSource
                  ? (kbPageContentData?.scanUrl ?? undefined)
                  : pageImageUrl
              }
              pageRotation={isKnowledgeBaseSource ? 0 : pageRotation}
            />
          )}
        </div>
      </ReportContainerContext.Provider>
    );
  }

  if (status.type === "failed") {
    return failedReportAlert;
  }

  if (status.type === "contentError") {
    return (
      <ReportStatusAlert
        titleId="chat.report.contentError"
        descriptionId="chat.report.contentErrorDescription"
        variant="destructive"
      />
    );
  }

  if (status.type === "empty") {
    return (
      <ReportStatusAlert
        titleId="chat.report.noContent"
        descriptionId="chat.report.noContentDescription"
        variant="default"
      />
    );
  }

  return (
    <div ref={setReportContainerEl}>
      <TermDefinitionDialog
        containerRef={reportContainerEl}
        termMap={termMap}
      />
      <HighlightedMarkdown
        withHeadings
        terms={terms}
        medicationMatcher={medicationMatcher}
      >
        {content}
      </HighlightedMarkdown>
    </div>
  );
};
